import React, { useState } from 'react';
import AppLayout from 'layouts/AppLayout';
import Icon from 'components/AppIcon';
import { useRole } from 'context/RoleContext';
import { ROLES } from 'config/roleConfig';
import { PERIODS } from './components/esgData';
import ESGScoreGauge from './components/ESGScoreGauge';
import KPICards from './components/KPICards';
import SustainabilityCharts from './components/SustainabilityCharts';
import ESGRadarAndAssets from './components/ESGRadarAndAssets';
import ImpactMetricsTable from './components/ImpactMetricsTable';
import ReportingPanel from './components/ReportingPanel';
import ESGAlertsPanel from './components/ESGAlertsPanel';

export default function ESGReportingDashboard() {
  const { role } = useRole();
  const canExport = role === ROLES.INVESTMENT_TEAM || role === ROLES.ASSET_MGMT_TEAM ||
    role === ROLES.FINANCE_TEAM || role === ROLES.PLATFORM_ADMIN;
  const [selectedPeriod, setSelectedPeriod] = useState('q3-2025');
  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  const handleExport = () => {
    showToast('ESG report export queued — you will be notified when ready.');
  };

  const handleShare = () => {
    showToast('Share link copied to clipboard.');
  };

  return (
    <AppLayout>
      {/* Toast */}
      {toast && (
        <div
          className="fixed top-5 right-5 flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg border border-border bg-card z-50 max-w-sm"
          style={{ zIndex: 'var(--z-toast)' }}
        >
          <Icon
            name={toast?.type === 'success' ? 'CheckCircle' : 'AlertCircle'}
            size={18}
            color={toast?.type === 'success' ? 'var(--color-success)' : 'var(--color-warning)'}
          />
          <span className="font-caption text-foreground text-sm">{toast?.message}</span>
          <button onClick={() => setToast(null)} className="ml-auto">
            <Icon name="X" size={14} color="var(--color-muted-foreground)" />
          </button>
        </div>
      )}
      <div className="min-h-screen bg-background">
        {/* Page Header */}
        <div
          className="sticky top-0 bg-background border-b border-border px-6 md:px-10 lg:px-12 py-4 flex items-center justify-between gap-4 flex-wrap"
          style={{ zIndex: 'var(--z-nav)' }}
        >
          <div className="pl-10 lg:pl-0">
            <h1 className="font-heading font-semibold text-foreground" style={{ fontSize: '1.375rem', lineHeight: 1.3 }}>
              ESG &amp; Reporting
            </h1>
            <p className="font-caption text-muted-foreground text-sm mt-0.5">
              Portfolio sustainability performance — Sample data, for demonstration purposes only
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {/* Period selector */}
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e?.target?.value)}
              className="border border-border rounded-lg px-3 py-2 font-caption text-sm bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {PERIODS?.map((p) => (
                <option key={p?.value} value={p?.value}>{p?.label}</option>
              ))}
            </select>
            {/* Export — internal roles only */}
            {canExport && (
              <button
                onClick={handleExport}
                className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border font-caption text-sm font-medium text-foreground bg-card hover:bg-muted transition-colors"
              >
                <Icon name="FileDown" size={15} color="var(--color-foreground)" />
                Export Report
              </button>
            )}
            {/* Share — internal roles only */}
            {canExport && (
              <button
                onClick={handleShare}
                className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border font-caption text-sm font-medium text-foreground bg-card hover:bg-muted transition-colors"
              >
                <Icon name="Share2" size={15} color="var(--color-foreground)" />
                Share
              </button>
            )}
          </div>
        </div>

        {/* Main content */}
        <div className="px-6 md:px-10 lg:px-12 py-8 space-y-6">

          {/* ROW 1 — Portfolio ESG Score Gauge + Sub-score Cards */}
          <ESGScoreGauge score={74} change={2.3} changePeriod="Q3 2025" />

          {/* ROW 2 — KPI Cards */}
          <KPICards />

          {/* ROW 3 — Sustainability Trend Charts */}
          <SustainabilityCharts />

          {/* ROW 4 — Radar Chart + Asset ESG Comparison */}
          <ESGRadarAndAssets />

          {/* ROW 5 — Impact Metrics Table */}
          <ImpactMetricsTable />

          {/* ROW 6 — Reporting Panel */}
          <ReportingPanel onToast={showToast} />

          {/* ROW 7 — ESG Alerts */}
          <ESGAlertsPanel onToast={showToast} />

          {/* Bottom padding */}
          <div className="h-8" />
        </div>
      </div>
    </AppLayout>
  );
}
